import React, { useRef } from "react";
import { ChevronLeft, ChevronRight, ArrowUpRight } from "lucide-react";
import { FEATURED, PREVIEW_DATE } from "@/components/landing/landingData";

const Card = ({ d, onOpen }) => (
  <article data-testid={`lp-feat-${d.key}`} className="group w-[85vw] max-w-[560px] shrink-0 snap-start overflow-hidden rounded-2xl border border-white/10 bg-[#060A12] transition-[border-color,transform] duration-300 hover:-translate-y-1 hover:border-[#22D3EE]/40 sm:w-[520px]">
    <div className="relative aspect-[1036/670] overflow-hidden border-b border-white/5 bg-black">
      <img src={d.preview} alt={`${d.name} dashboard preview`} loading="lazy" className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.02]" />
      <span className="absolute bottom-3 left-3 rounded-full bg-[#22D3EE] px-2.5 py-1 font-mono text-[9px] font-bold uppercase tracking-widest text-black">Live</span>
      <span className="absolute bottom-3 right-3 rounded-full bg-black/70 px-2.5 py-1 font-mono text-[9px] uppercase tracking-widest text-slate-300">Snapshot · {PREVIEW_DATE}</span>
    </div>
    <div className="p-5">
      <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#22D3EE]/80">{d.sport}</p>
      <h3 className="mt-1 font-condensed text-2xl font-extrabold uppercase tracking-wide text-white">{d.name}</h3>
      <p className="mt-2 text-sm leading-relaxed text-slate-400">{d.desc}</p>
      <div className="mt-4 flex flex-wrap gap-2">
        {d.tags.map((t) => <span key={t} className="rounded-full border border-white/10 px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-slate-300">{t}</span>)}
      </div>
      <button data-testid={`lp-feat-open-${d.key}`} onClick={() => onOpen(d.key, d.name)} className="mt-5 inline-flex items-center gap-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-[#22D3EE] transition-colors hover:text-white">
        Open Dashboard <ArrowUpRight className="h-4 w-4" />
      </button>
    </div>
  </article>
);

export const Showcase = ({ onOpen }) => {
  const ref = useRef(null);
  const step = (dir) => ref.current?.scrollBy({ left: dir * 540, behavior: "smooth" });
  return (
    <section data-testid="lp-showcase" className="relative border-t border-white/5 bg-[#03050A] py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="flex items-end justify-between gap-6">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-[#22D3EE]">Featured dashboards</p>
            <h2 className="mt-3 font-condensed text-4xl font-extrabold uppercase tracking-tight text-white sm:text-5xl">Inside Hat Trick</h2>
            <p className="mt-2 max-w-xl text-sm text-slate-400">Real screens from the app. Previews are snapshots, not live data.</p>
          </div>
          <div className="hidden gap-2 sm:flex">
            <button data-testid="lp-showcase-prev" aria-label="Previous dashboards" onClick={() => step(-1)} className="rounded-full border border-white/15 p-3 text-white transition-colors hover:border-[#22D3EE]/60"><ChevronLeft className="h-4 w-4" /></button>
            <button data-testid="lp-showcase-next" aria-label="Next dashboards" onClick={() => step(1)} className="rounded-full border border-white/15 p-3 text-white transition-colors hover:border-[#22D3EE]/60"><ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>
      </div>
      <div ref={ref} data-testid="lp-showcase-track" className="lp-scroll mt-10 flex snap-x snap-mandatory scroll-pl-4 sm:scroll-pl-6 lg:scroll-pl-[max(1.5rem,calc((100vw_-_80rem)/2_+_1.5rem))] gap-5 overflow-x-auto px-4 pb-4 sm:px-6 lg:px-[max(1.5rem,calc((100vw_-_80rem)/2_+_1.5rem))]">
        {FEATURED.map((d) => <Card key={d.key} d={d} onOpen={onOpen} />)}
      </div>
    </section>
  );
};
